import { MappingActionTypes, MappingContext } from 'contexts/MappingContext';
import { CACHE_FIRST } from 'contracts/constants';
import type { ICollectionType, ISchema } from 'contracts/interfaces';
import type { FC } from 'react';
import { useContext, useEffect } from 'react';
import { useGetMappingQuery, useGetModelQuery, useGetPackageVersionQuery } from 'services/graphql/generated/hooks';
import type { IContainerChildrenProps } from 'utils/IContainerChildrenProps';
import { isPackageEditable } from 'utils/is-package-editable';

interface IMappingContextLoaderProps {
  locked?: boolean;
  versionUuid: string;
  entityId: string;
}

const MappingContextLoader: FC<IMappingContextLoaderProps & IContainerChildrenProps> = ({
  versionUuid,
  entityId,
  locked,
  children
}) => {
  const ctx = useContext(MappingContext);

  const { loading: loadingVersion, data: versionData } = useGetPackageVersionQuery({
    variables: {
      uuid: versionUuid
    },
    skip: !versionUuid,
    ...CACHE_FIRST
  });

  const { loading: loadingMapping, data: mappingData } = useGetMappingQuery({
    variables: {
      packageVersionUuid: versionUuid,
      uuid: entityId
    },
    skip: !entityId,
    ...CACHE_FIRST
  });
  const { loading: loadingSourceModel, data: sourceModelData } = useGetModelQuery({
    variables: {
      packageVersionUuid: versionUuid,
      uuid: mappingData?.getMapping.schema._meta?.source
    },
    skip: !mappingData?.getMapping.schema._meta?.source,
    ...CACHE_FIRST
  });
  const { loading: loadingTargetModel, data: targetModelData } = useGetModelQuery({
    variables: {
      packageVersionUuid: versionUuid,
      uuid: mappingData?.getMapping.schema._meta?.target
    },
    skip: !mappingData?.getMapping.schema._meta?.target,
    ...CACHE_FIRST
  });
  useEffect(() => {
    if (
      mappingData &&
      sourceModelData &&
      targetModelData &&
      (!ctx?.state.uuid || ctx?.state.uuid !== mappingData.getMapping.uuid)
    ) {
      ctx?.dispatch({
        type: MappingActionTypes.INIT_MAPPING,
        payload: {
          mapping: {
            schema: mappingData.getMapping.schema as ICollectionType,
            uuid: entityId,
            packageVersionUuid: versionUuid,
            name: mappingData.getMapping.name,
            description: mappingData.getMapping.description
          },
          source: {
            schema: sourceModelData.getModel.schema as ISchema,
            packageVersionUuid: versionUuid,
            uuid: sourceModelData.getModel.uuid,
            name: sourceModelData.getModel.name,
            description: sourceModelData.getModel.description
          },
          target: {
            schema: targetModelData.getModel.schema as ISchema,
            packageVersionUuid: versionUuid,
            uuid: targetModelData.getModel.uuid,
            name: targetModelData.getModel.name,
            description: targetModelData.getModel.description
          },
          locked: !isPackageEditable(versionData?.getPackageVersion.status)
        }
      });
    }
  }, [mappingData, sourceModelData, targetModelData, versionData, ctx?.state.uuid]);

  const loading =
    !(mappingData && sourceModelData && targetModelData) ||
    loadingMapping ||
    loadingSourceModel ||
    loadingTargetModel ||
    ctx?.state.uuid !== entityId;

  return <>{loading ? 'Loading' : children}</>;
};

export default MappingContextLoader;
